import React, { ChangeEvent, useEffect, useState } from 'react';

import { ValueBox, ValueGroup, ValueInput, ValueToggle } from '@candu-ai/craft/ui';
import { getFlexValues } from 'craft/containers/Toolbox/sections/Layout/utils';
import { useResponsiveCss } from 'craft/hooks';
import { CommonNodeProperties, CssSelectorId } from 'craft/types';

export interface FlexChildLayoutProps {
  nodeIds: string[];
  cssSelectorId?: CssSelectorId;
}

export const FlexChildLayoutSettings = ({ nodeIds, cssSelectorId }: FlexChildLayoutProps) => {
  const { responsiveCss, setResponsiveCss } = useResponsiveCss(
    nodeIds,
    CommonNodeProperties.Multiple,
    cssSelectorId,
  );

  const { isGrowActive, isShrinkActive, basisValue } = getFlexValues(responsiveCss || {});

  const [inputValue, setInputValue] = useState<string | undefined>(basisValue);

  const onSetValue = (newValue?: string) => {
    setInputValue(newValue);
    setResponsiveCss({ flexBasis: (newValue || 'auto').toString() });
  };

  const onBlur = () => {
    if (inputValue === '') {
      // if value is empty, apply inherited or default
      setResponsiveCss({ flexBasis: '' });
    }
  };

  useEffect(() => {
    if (basisValue === inputValue) {
      return;
    }

    // when value changes due to external factors reflect it in the input

    if (inputValue === '' && basisValue === 'auto') {
      // if the input value is being deleted, reflect empty value in input without changing the value
      setInputValue('');
    } else {
      setInputValue(basisValue ?? '');
    }
  }, [basisValue]);

  return (
    <ValueGroup>
      <ValueBox label="Grow if possible" isInGroup={{ isFirst: true }}>
        <ValueToggle
          data-cy="toggle-flex-grow"
          toggleGroup="flex-grow"
          id="flex-child-toggle-flex-grow"
          onClick={() => setResponsiveCss({ flexGrow: isGrowActive ? 0 : 1 })}
          isActive={isGrowActive}
          tooltip="flex-grow"
          color="light"
        />
      </ValueBox>
      <ValueBox label="Shrink if necessary" isInGroup>
        <ValueToggle
          data-cy="toggle-flex-shrink"
          toggleGroup="flex-shrink"
          id="toggle-flex-shrink"
          onClick={() => setResponsiveCss({ flexShrink: isShrinkActive ? 0 : 1 })}
          isActive={isShrinkActive}
          tooltip="flex-shrink"
          color="light"
        />
      </ValueBox>
      <ValueBox label="Flex Base size" isInGroup={{ isLast: true }}>
        <ValueInput
          data-cy="input-flex-basis"
          id="flex-basis-field"
          value={inputValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onSetValue(e.target.value)}
          onBlur={onBlur}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              onBlur();
            }
          }}
        />
      </ValueBox>
    </ValueGroup>
  );
};
